/**
 * FACTORY FUNCTION
 *
 * The Syncfusion components only renders child components if they are passed as functions.
 * I created this factory method to facilitate interaction with new components as needed.
 * To use it, simply add the desired properties to the options object and handle the logic within the component.
 *
 * @param {Vue.Component} component - The Vue component to be extended
 * @param {Object} options - An object containing props to pass to the component
 * @returns {Function} - A function that returns a Vue component
 */
export const componentMountFactory = (component, options) => {
  return () => {
    const safeComponent = component || 'div';
    return {
      components: {
        WidgetComponent: safeComponent,
      },
      data() {
        return { options: options || {} };
      },
      template: '<WidgetComponent v-bind="options" />',
    };
  };
};

/**
 * ID GENERATOR FUNCTION
 *
 * We are using ids based on component creation timestamp
 * We are using the ID of each cell based on the time it was added to the dashboard
 * This way, we won't duplicate any cell and will also retain useful information
 * @returns {string} - creation timestamp
 */
export const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// *  Below variables refer to ejs-dashboardlayout component properties
/**
 * The number of columns in the panel
 * @type {number}
 */
export const numberOfColumns = 4;

/**
 * Gap between the cells in the layout
 * @type {number[]}
 */
export const cellSpacing = [10, 10];

/**
 * The aspect ratio of the cell in the panel
 * (i.e. with value of 2, for every 100 px of width, 50 px will be the height of the cell)
 * @type {number}
 */
export const cellAspectRatio = 2;

/**
 * Directions in which the layout cell can be resized
 * @type {string[]}
 */
export const resizableHandles = [
  'e-south-east',
  'e-east',
  'e-west',
  'e-north',
  'e-south',
];

/**
 * Option to set whether cells cannot have empty spaces above them
 * Example:
 * If a cell is in row 3 but there are no cells above it (in the rows above),
 * with this option enabled, the cell will 'float' up.
 * @type {boolean}
 */
export const allowFloating = true;

/**
 * Default size and positioning options of dashboard cell
 * @type {Object}
 */
export const defaultCellOptions = {
  sizeX: 2,
  sizeY: 2,
  row: 0,
  col: 0,
};

/**
 * The max number of widgets in a single dashboard
 * @type {number}
 */
export const maxWidgetsInDashboard = 10;

/**
 * The color scheme for message metrics widgets.
 * This object defines the colors used to represent outgoing and incoming messages in the widgets.
 * @type {Object}
 * @property {string} outgoing - The color used for outgoing messages.
 * @property {string} incoming - The color used for incoming messages.
 */
export const messagesMetricColors = {
  outgoing: '#FFD700', // Dourado
  incoming: '#570095', // Roxo
  total: '#1f93ff', // Azul claro
};

/**
 * The color scheme for resolution reasons metrics widgets.
 * This object defines the colors used to represent different resolution reasons in the widgets.
 * @type {Object}
 * @property {string} 'Objective Achieved' - The color used for the 'Objective Achieved' reason.
 * @property {string} 'Objective Not Achieved' - The color used for the 'Objective Not Achieved' reason.
 * @property {string} 'Doubt' - The color used for the 'Doubt' reason.
 * @property {string} 'Other' - The color used for the 'Other' reason.
 */
export const resolutionReasonsMetricColors = {
  objectiveAchieved: '#4CAF50', // Verde
  objectiveNotAchieved: '#F44336', // Vermelho
  doubt: '#FF9800', // Laranja
  other: '#9E9E9E', // Cinza
};

export const resolutionReasonsSummaryColor = {
  objectiveAchieved: '#A5D6A7', // Verde claro
  objectiveNotAchieved: '#EF9A9A', // Vermelho claro
  doubt: '#FFCC80', // Laranja claro
  other: '#E0E0E0', // Cinza claro
};

/**
 * List of submetric types for conversation widgets, including their titles with internationalization keys.
 * Each submetric object contains:
 * - type: A string representing the type of submetric.
 * - title: An object containing:
 *   - i18nKey: The i18n key for the metric's name.
 *   - additional: Any additional text to append to the metric's title.
 * @type {Object[]}
 */
export const conversationSubmetrics = [
  {
    type: 'conversations_count',
    title: {
      i18nKey: 'REPORT.METRICS.CONVERSATIONS.NAME',
      additional: ' (Total)',
    },
  },
  {
    type: 'resolutions_count',
    title: {
      i18nKey: 'REPORT.METRICS.RESOLUTION_COUNT.NAME',
      additional: '',
    },
  },
  {
    type: 'avg_first_response_time',
    title: {
      i18nKey: 'REPORT.METRICS.FIRST_RESPONSE_TIME.NAME',
      additional: '',
    },
  },
  {
    type: 'avg_resolution_time',
    title: {
      i18nKey: 'REPORT.METRICS.RESOLUTION_TIME.NAME',
      additional: '',
    },
  },
];

export const conversationResolutionReasonSubmetrics = [
  {
    type: 'objective_achieved',
    title: {
      i18nKey:
        'CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.OBJECTIVE_ACHIEVED',
      additional: '',
    },
  },
  {
    type: 'objective_not_achieved',
    title: {
      i18nKey:
        'CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.OBJECTIVE_NOT_ACHIEVED',
      additional: '',
    },
  },
  {
    type: 'doubt',
    title: {
      i18nKey: 'CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.DOUBT',
      additional: '',
    },
  },
  {
    type: 'other',
    title: {
      i18nKey: 'CUSTOM_DASHBOARDS.CONVERSATION_RESOLUTION_REASONS.OTHER',
      additional: '',
    },
  },
];

// COLUMN CHART RESPONSIBLE OPTIONS
export const smallest_Sizes = {
  containerWidth: '200px',
  containerHeight: '100px',
  containerPaddingTop: '110px',
  yAxisLabelStyle: '0.5rem',
  xAxisLabelStyle: '0.5rem',
};
export const smaller_1_Sizes = {
  containerWidth: '250px',
  containerHeight: '125px',
  containerPaddingTop: '100px',
  yAxisLabelStyle: '0.65rem',
  xAxisLabelStyle: '0.55rem',
};
export const smaller_2_Sizes = {
  containerWidth: '290px',
  containerHeight: '145px',
  containerPaddingTop: '40px',
  yAxisLabelStyle: '0.65rem',
  xAxisLabelStyle: '0.55rem',
};
export const medium_1_Sizes = {
  containerWidth: '300px',
  containerHeight: '150px',
  containerPaddingTop: '25px',
  yAxisLabelStyle: '0.8rem',
  xAxisLabelStyle: '0.65rem',
};
export const medium_2_Sizes = {
  containerWidth: '420px',
  containerHeight: '210px',
  containerPaddingTop: '25px',
  yAxisLabelStyle: '0.9rem',
  xAxisLabelStyle: '0.65rem',
};
export const biggerSizes = {
  containerWidth: '600px',
  containerHeight: '300px',
  containerPaddingTop: '15px',
  yAxisLabelStyle: '1.2rem',
  xAxisLabelStyle: '0.8rem',
};
// ----------------------------------------------------------------

// TABLE INDICATOR RESPONSIBLE OPTIONS
export const table_smallest_Sizes = {
  id: 1,
  containerWidth: '230px',
  containerHeight: '100px',
  containerPaddingTop: '300px',
  containerPaddingBottom: '10px',
  cellPadding: '4px',
  yAxisLabelStyle: '0.5rem',
  xAxisLabelStyle: '0.5rem',
  fontSize: '10px',
};
export const table_smallest_1_Sizes = {
  id: 1.1,
  containerWidth: '270px',
  containerHeight: '100px',
  containerPaddingTop: '270px',
  containerPaddingBottom: '10px',
  cellPadding: '4px',
  yAxisLabelStyle: '0.5rem',
  xAxisLabelStyle: '0.5rem',
  fontSize: '12px',
};
export const table_smaller_1_Sizes = {
  id: 2,
  containerWidth: '230px',
  containerHeight: '125px',
  containerPaddingTop: '200px',
  containerPaddingBottom: '10px',
  cellPadding: '4px',
  yAxisLabelStyle: '0.65rem',
  xAxisLabelStyle: '0.55rem',
  fontSize: '10px',
};
export const table_smaller_2_Sizes = {
  id: 3,
  containerWidth: '270px',
  containerHeight: '125px',
  containerPaddingTop: '130px',
  containerPaddingBottom: '10px',
  cellPadding: '4px',
  yAxisLabelStyle: '0.65rem',
  xAxisLabelStyle: '0.55rem',
  fontSize: '12px',
};
export const table_smaller_3_Sizes = {
  id: 4,
  containerWidth: '300px',
  containerHeight: '125px',
  containerPaddingTop: '60px',
  containerPaddingBottom: '10px',
  cellPadding: '4px',
  yAxisLabelStyle: '0.65rem',
  xAxisLabelStyle: '0.55rem',
  fontSize: '14px',
};
export const table_medium_1_Sizes = {
  id: 5,
  containerWidth: '300px',
  containerHeight: '150px',
  containerPaddingTop: '200px',
  containerPaddingBottom: '10px',
  cellPadding: '4px',
  yAxisLabelStyle: '0.8rem',
  xAxisLabelStyle: '0.65rem',
  fontSize: '15px',
};
export const table_medium_2_Sizes = {
  id: 6,
  containerWidth: '420px',
  containerHeight: '210px',
  containerPaddingTop: '25px',
  containerPaddingBottom: '10px',
  cellPadding: '4px',
  yAxisLabelStyle: '0.9rem',
  xAxisLabelStyle: '0.65rem',
  fontSize: '15px',
};
export const table_biggerSizes = {
  id: 7,
  containerWidth: '600px',
  containerHeight: '300px',
  containerPaddingTop: '15px',
  containerPaddingBottom: '10px',
  cellPadding: '4px',
  yAxisLabelStyle: '1.2rem',
  xAxisLabelStyle: '0.8rem',
  fontSize: '15px',
};
// ----------------------------------------------------------------
